import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Advent of Code Day 20 – Particle Swarm",
  "description": "",
  "published": "2017-12-20T00:00:00.000Z",
  "redirect_from": ["/blog/advent-code-day-20-particle-swarm/swizec/7989"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "ekm7m9CDnBQ",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Day 20 smells like one of those problems that you have to math.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`OMG I MADE LEADERBOARD FOR DAY 20!!`}<a parentName="p" {...{
          "href": "https://t.co/Jn5odRVtVM"
        }}>{`https://t.co/Jn5odRVtVM`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/AdventOfCode?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#AdventOfCode`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/iDOF1sLREm"
        }}>{`pic.twitter.com/iDOF1sLREm`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/943380787528835072"
      }}>{`December 20, 2017`}</a></blockquote>
    <p>{`You're given a large list of particles that each have a velocity vector and an acceleration vector. Velocity tells you where they move and how fast. Acceleration tells you how velocity changes.`}</p>
    <p>{`You then have to figure out which particle will stay closest to the center – `}<inlineCode parentName="p">{`<0,0,0>`}</inlineCode>{` – in the "long term".`}</p>
    <p>{`You can math this.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/advent-of-code-day20-particles-1024x575.jpg",
        "alt": null
      }}></img></p>
    <p>{`The particle that is `}<em parentName="p">{`accelerating`}</em>{` away from center the slowest is the particle that will eventually stay the closest.`}</p>
    <p>{`This makes sense when you think about it. No matter the initial position or velocity, each particle is either accelerating towards the center, or a way from it. Even if a particle is moving towards the center, it can still be accelerating away from center. Eventually it will fly far far away.`}</p>
    <p>{`Say a particle starts at `}<inlineCode parentName="p">{`p = <3,0,0>`}</inlineCode>{` and is moving away from center at `}<inlineCode parentName="p">{`v = <3,0,0>`}</inlineCode>{`, but its acceleration is towards center – `}<inlineCode parentName="p">{`a = <-1,0,0>`}</inlineCode>{`. If we iterate through each tick of the simulation, you'll see what I mean 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`p = <3,0,0>  v=<3,0,0>  a = <-1,0,0>
p = <5,0,0>  v=<2,0,0>  a = <-1,0,0>
p = <6,0,0>  v=<1,0,0>  a = <-1,0,0>
p = <6,0,0>  v=<0,0,0>  a = <-1,0,0>
p = <5,0,0>  v=<-1,0,0> a = <-1,0,0>
p = <3,0,0>  v=<-2,0,0> a = <-1,0,0>
p = <0,0,0>  v=<-3,0,0> a = <-1,0,0>
...
`}</code></pre>
    <p>{`On each tick of the simulation, we add acceleration to velocity, then add velocity to the current position. Our particle that started life flying away from center first slows down, then stops, then hurtles towards center.`}</p>
    <p>{`After 4 ticks, it reaches center and keeps flying away ever faster.`}</p>
    <p>{`That's what I mean when by "accelerating away from center". With enough time `}<strong parentName="p">{`all`}</strong>{` particles are accelerating away from center because if they were ever going to cross it, they already have.`}</p>
    <p>{`But math is hard and modern computers are fast. Even in JavaScript.`}</p>
    <h2 {...{
      "id": "star-1"
    }}>{`Star 1`}</h2>
    <p>{`So we can `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/advent-of-code-2017/blob/master/20.js#L1037"
      }}>{`brute force the solution`}</a>{`. No math required 😇`}</p>
    <p>{`The gist of our bruteforcing is the simulation function itself, the `}<inlineCode parentName="p">{`tick`}</inlineCode>{`. It goes through all particles and moves them.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function tick(particles) {
  return particles.map(({ p, v, a }) => {
    v = v.map((n, i) => n + a[i]);
    return {
      p: p.map((n, i) => n + v[i]),
      v: v,
      a: a,
    };
  });
}
`}</code></pre>
    <p>{`That returns a new copy of the `}<inlineCode parentName="p">{`particles`}</inlineCode>{` array with changed positions, `}<inlineCode parentName="p">{`p`}</inlineCode>{`, and velocities, `}<inlineCode parentName="p">{`v`}</inlineCode>{`. Acceleration doesn't change.`}</p>
    <p>{`We then also need a way to find the particle closest to center. That's these two functions.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function distance(particle) {
  return particle.p.reduce((d, n) => d + Math.abs(n), 0);
}

function closest(particles) {
  return particles.reduce(
    ({ min, index }, p, i) =>
      distance(p) < min
        ? { min: distance(p), index: i }
        : { min: min, index: index },
    { min: distance(particles[0]), index: 0 }
  );
}
`}</code></pre>
    <p><inlineCode parentName="p">{`distance`}</inlineCode>{` gives us the `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Taxicab_geometry"
      }}>{`Manhattan distance`}</a>{` between the center and a particle. `}<inlineCode parentName="p">{`closest`}</inlineCode>{` finds the particle with the smallest distance and returns its `}<inlineCode parentName="p">{`index`}</inlineCode>{` and distance `}<inlineCode parentName="p">{`min`}</inlineCode>{`.`}</p>
    <p>{`Then we just run the simulation until the closest particle doesn't change for a while. I got good results with `}<inlineCode parentName="p">{`"hasn't changed in 2000 ticks"`}</inlineCode>{`. Some fiddling required.`}</p>
    <p>{`Generally the larger that number, the more accurate your result. If you describe the problem in terms of search algorithms (you're searching for a stable state), then any sequence of stable states when the `}<inlineCode parentName="p">{`min`}</inlineCode>{` distance doesn't change for a while is a local maximum. You're looking for the global maximum.`}</p>
    <p>{`The longer you keep looking, the likelier that you're at the best possible result.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function star1(input) {
  let particles = prepInput(input);

  let stable = {
    min: 0,
    index: 0,
    iterations_unchanged: 0,
  };

  while (stable.iterations_unchanged < 2000) {
    particles = tick(particles);

    const { min, index } = closest(particles);

    if (stable.index === index) {
      stable.iterations_unchanged += 1;
    } else {
      stable.min = min;
      stable.index = index;
      stable.iterations_unchanged = 0;
    }
  }

  console.log(stable);
}
`}</code></pre>
    <p>{`Keep track of the result and how long it's remained unchanged. Repeat until desired accuracy.`}</p>
    <p>{`Runs in about a second on my machine. 👌`}</p>
    <h2 {...{
      "id": "star-2"
    }}>{`Star 2`}</h2>
    <p>{`Star 2 really stretched what I thought was possible with JavaScript: Finding collisions.`}</p>
    <p>{`When particles overlap, they are removed from the pool. Your goal is to find how many particles are left standing after "long enough".`}</p>
    <p>{`The question can once gain be translated into "What's the steady state?".`}</p>
    <p>{`But how do you find colliding particles?`}</p>
    <p>{`The simplest approach is to compare every particle with every particle. Surprisingly, that's good enough. Modern computers are `}<em parentName="p">{`fast`}</em>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Collision_detection"
      }}>{`Collision detection`}</a>{` is a huge field of algorithmics, by the way. It is definitely not supposed to be as easy as comparing every particle with every particle.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function collision(particles) {
  return particles.map((particle, idx) => {
    const {
      p: [x, y, z],
    } = particle;

    if (
      particles.find(
        ({ p }, i) => i !== idx && p[0] == x && p[1] == y && p[2] == z
      )
    ) {
      particle.dead = true;
    }
    return particle;
  });
}
`}</code></pre>
    <p>{`Returns a new copy of the `}<inlineCode parentName="p">{`particles`}</inlineCode>{` array with every particle for which a particle exists in the same position marked as `}<inlineCode parentName="p">{`dead`}</inlineCode>{`.`}</p>
    <p>{`To run the experiment, we then use a similar setup as before 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function star2(input) {
  let particles = prepInput(input);

  let stable = {
    length: particles.length,
    iterations_unchanged: 0,
  };

  while (stable.iterations_unchanged < 2000) {
    particles = collision(tick(particles)).filter(({ dead }) => !dead);

    if (stable.length === particles.length) {
      stable.iterations_unchanged += 1;
    } else {
      stable.length = particles.length;
      stable.iterations_unchanged = 0;
    }
  }

  console.log(stable);
}
`}</code></pre>
    <p>{`Keep track of stable state and how long it's been stable for. Run the `}<inlineCode parentName="p">{`tick`}</inlineCode>{` simulation, detection `}<inlineCode parentName="p">{`collision`}</inlineCode>{`, and `}<inlineCode parentName="p">{`filter`}</inlineCode>{` dead particles.`}</p>
    <p>{`After a few seconds, the result pops out.`}</p>
    <p>{`Paying `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/first-day-new-2017-macbook-pro/swizec/7643"
      }}>{`\\`}{`\\`}{`\\$3,000 for a laptop`}</a>{` was very worth it 👌`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      